/**
 * Lo reescribe el editor de mapas, igual que `rooms.ts`.
 *
 * Se mantiene ordenado alfabéticamente por el reescritor. Editarlo a mano funciona;
 * corre `npm run check:collisions` después.
 */
export const ART_SHAPES = {
    /*
     * `/art/props/balon/balon-volley.svg` — 100x108.
     *
     * Un octógono sobre la bola, sin la sombra: la sombra está dibujada debajo para que el
     * balón se lea apoyado, pero es piso, y con la caja entera se chocaba contra ella.
     */
    "balon-volley": {
        aspect: 0.9259,
        points: [
            { x: 0.5, y: 0.056 },
            { x: 0.811, y: 0.175 },
            { x: 0.94, y: 0.463 },
            { x: 0.811, y: 0.751 },
            { x: 0.5, y: 0.87 },
            { x: 0.189, y: 0.751 },
            { x: 0.06, y: 0.463 },
            { x: 0.189, y: 0.175 },
        ],
    },
    /* `/art/props/mesa/taws-mesa.png` — 2621x1280. Sin silueta todavía: estorba su caja. */
    mesa: { aspect: 2.0477 },
    /*
     * `/art/props/mesa/mesaspcs.svg` — 622.5x523.5.
     *
     * Los vértices salen de la máscara del propio SVG, muestreada y simplificada, no de
     * mirar el dibujo a ojo. Es una banda diagonal que baja de izquierda a derecha: las
     * esquinas de arriba a la derecha y de abajo a la izquierda están vacías, y con la
     * caja cruda el jugador chocaba contra las dos.
     */
    "mesa-pcs": {
        aspect: 1.1891,
        points: [
            { x: 0.0092, y: 0 },
            { x: 0.2615, y: 0 },
            { x: 0.5569, y: 0.3256 },
            { x: 0.8831, y: 0.4286 },
            { x: 0.9723, y: 0.6312 },
            { x: 1, y: 0.8904 },
            { x: 1, y: 1 },
            { x: 0.64, y: 1 },
            { x: 0.3662, y: 0.9601 },
            { x: 0.2615, y: 0.7442 },
            { x: 0.0769, y: 0.4252 },
            { x: 0, y: 0.2425 },
        ],
    },
    /*
     * `/art/props/arbol/arbol.png` — 552x512, recortado a su contenido.
     *
     * Moldeada desde la biblioteca de siluetas, y hacía falta: la caja entera de un árbol
     * estorba de más en las esquinas de abajo, donde sólo hay tronco, y se vivía como
     * chocar con aire al pasar por el costado. La silueta sigue la copa y baja al tronco.
     */
    arbol: {
        aspect: 1.0781,
        points: [
            { x: 0, y: 0 },
            { x: 1, y: 0 },
            { x: 0.7235, y: 0.2922 },
            { x: 0.6816, y: 0.6175 },
            { x: 0.6927, y: 0.994 },
            { x: 0.5642, y: 0.9819 },
            { x: 0.2877, y: 0.8223 },
            { x: 0.3184, y: 0.3404 },
            { x: 0.0419, y: 0.2139 },
            { x: 0, y: 0.1657 },
        ],
    },
    /* `/art/props/silla/sillarosa.png` — 2048x1971. */
    silla: {
        aspect: 1.0391,
        points: [
            { x: 0.4251, y: 0.0487 },
            { x: 0.7324, y: 0.1106 },
            { x: 0.7327, y: 0.9485 },
            { x: 0.4653, y: 1 },
            { x: 0.1188, y: 0.9381 },
            { x: 0.1674, y: 0.4611 },
            { x: 0.3954, y: 0.2962 },
        ],
    },
    "mesa-taws": {
        aspect: 1.101,
        points: [
            { x: 0.8094, y: 0 },
            { x: 1, y: 0 },
            { x: 1, y: 0.2062 },
            { x: 0.4004, y: 1 },
            { x: 0, y: 1 },
            { x: 0, y: 0.6434 },
        ],
    },
    carrito: {
        aspect: 1.7936,
        points: [
            { x: 0.8091, y: 0.5598 },
            { x: 0.8091, y: 0.7602 },
            { x: 0.8318, y: 0.8092 },
            { x: 0.7205, y: 0.9645 },
            { x: 0.2273, y: 0.7275 },
            { x: 0.2818, y: 0.6662 },
            { x: 0.2227, y: 0.6213 },
            { x: 0.2303, y: 0.4565 },
        ],
    },
};
/** Mínimo de vértices de una silueta, igual que el resto de polígonos del sistema. */
export const MIN_COLLISION_VERTICES = 3;
/**
 * Cuánto puede desviarse la proporción de un rect antes de contar como deformado.
 *
 * Holgado a propósito: los rects se guardan como enteros, así que una caja de 325 de
 * ancho no puede tener exactamente 273.32 de alto. Con este margen, redondear no
 * dispara el aviso y deformar de verdad sí.
 */
export const ASPECT_TOLERANCE = 0.02;
export function artShape(key) {
    return ART_SHAPES[key] ?? null;
}
/** La proporción declarada para este dibujo, si hay alguna. */
export function aspectOf(key) {
    return ART_SHAPES[key]?.aspect ?? null;
}
/** ¿Esta caja respeta la proporción de su dibujo? Sin proporción declarada, sí. */
export function respectsAspect(rect, key) {
    const aspect = aspectOf(key);
    if (aspect === null || rect.height <= 0)
        return true;
    return Math.abs(rect.width / rect.height - aspect) <= ASPECT_TOLERANCE;
}
/**
 * Ajusta una caja a la proporción de su dibujo, conservando el ANCHO.
 *
 * El ancho manda porque es lo que se arrastra al colocar: uno decide cuán grande es el
 * mueble mirando lo que ocupa a lo ancho, y que el alto se acomode solo se lee como que
 * el objeto tiene su tamaño natural. Al revés —fijar el alto— un arrastre horizontal
 * movería el objeto en vertical, que se siente roto.
 */
export function fitToAspect(rect, key) {
    const aspect = aspectOf(key);
    if (aspect === null || aspect <= 0)
        return rect;
    return { ...rect, height: Math.max(1, Math.round(rect.width / aspect)) };
}
/** Fracciones del rect -> coordenadas de mundo. */
export function toWorld(points, rect) {
    return points.map((point) => ({
        x: rect.x + point.x * rect.width,
        y: rect.y + point.y * rect.height,
    }));
}
/**
 * Coordenadas de mundo -> fracciones del rect, recortadas a `0..1`.
 *
 * El recorte no es defensivo, es la regla: la caja del objeto delimita la de colisión.
 * Un vértice arrastrado fuera se queda pegado al borde en vez de rechazarse, que es lo
 * que uno espera al arrastrar contra un tope.
 */
export function toFractions(points, rect) {
    const width = rect.width || 1;
    const height = rect.height || 1;
    return points.map((point) => ({
        x: clamp01((point.x - rect.x) / width),
        y: clamp01((point.y - rect.y) / height),
    }));
}
function clamp01(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.min(1, Math.max(0, value));
}
/** Las cuatro esquinas en fracciones: la silueta por defecto, la caja entera. */
export function fullBox() {
    return [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
    ];
}
//# sourceMappingURL=artShapes.js.map